import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/services/supabase'
import { getProfile } from '@/services/api'
import { getCampaign, isCampaignRecorded, markCampaignRecorded } from '@/lib/campaign'
import type { User, Session } from '@supabase/supabase-js'
import type { ProfileWithMetadata } from '@/types'

/**
 * AuthProvider Component
 * Provides authentication context to the entire application.
 *
 * Based on FLOW-01-AUTHENTICATION.md
 *
 * - Session: mirrored from Supabase's onAuthStateChange. INITIAL_SESSION marks
 *   the end of the initial auth check (it fires after the client has restored
 *   and, if needed, refreshed the stored session).
 * - Profile: a React Query keyed by user ID, so it retries on failure,
 *   refetches on window focus, and responds to invalidateQueries(['profile']).
 * - Cache: cleared when the signed-in user goes away or changes, so one
 *   user's data never leaks to the next.
 *
 * The onAuthStateChange callback MUST stay synchronous. supabase-js awaits it
 * while holding its internal auth lock; awaiting any Supabase call in it (a
 * query calls getSession(), which waits on that lock) deadlocks the client.
 * That is what froze returning users on TOKEN_REFRESHED.
 */

export interface AuthContextValue {
  /** Current authenticated user */
  user: User | null
  /** Current session */
  session: Session | null
  /** User profile with metadata (tier, etc.) */
  profile: ProfileWithMetadata | null
  /** Whether auth state is being determined */
  isLoading: boolean
  /** Whether user is authenticated */
  isAuthenticated: boolean
  /** Whether user is an admin */
  isAdmin: boolean
  /** Refresh the profile data */
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient()
  const [session, setSession] = useState<Session | null>(null)
  const [isAuthReady, setIsAuthReady] = useState(false)

  // User the query cache currently belongs to
  const cacheUserIdRef = useRef<string | null>(null)

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      // Synchronous on purpose - see the component comment.
      console.log('[Auth] onAuthStateChange:', event, newSession ? 'has session' : 'no session')

      const nextUserId = newSession?.user?.id ?? null
      if (cacheUserIdRef.current !== null && cacheUserIdRef.current !== nextUserId) {
        queryClient.clear()
        console.log('[Auth] Cache cleared (user signed out or changed)')
      }
      cacheUserIdRef.current = nextUserId

      setSession(newSession)
      if (event === 'INITIAL_SESSION') setIsAuthReady(true)
    })

    return () => subscription.unsubscribe()
  }, [queryClient])

  const user = session?.user ?? null
  const userId = user?.id ?? null

  const profileQuery = useQuery({
    queryKey: ['profile', userId],
    queryFn: getProfile,
    enabled: userId !== null,
  })

  const refreshProfile = useCallback(async () => {
    if (userId) {
      await queryClient.refetchQueries({ queryKey: ['profile', userId] })
    }
  }, [userId, queryClient])

  const profile = profileQuery.data ?? null

  // Email campaign attribution (privacy §3.10): once signed in, send the campaign this tab
  // arrived with. The server keeps it only for new accounts, first write wins.
  const profileId = profile?.id ?? null
  useEffect(() => {
    if (!profileId || isCampaignRecorded()) return
    const campaign = getCampaign()
    if (!campaign) return
    markCampaignRecorded()
    supabase.rpc('record_signup_campaign', { p_campaign: campaign }).then(({ error }) => {
      if (error) console.warn('[Auth] record_signup_campaign failed:', error.message)
    })
  }, [profileId])

  const value: AuthContextValue = {
    user,
    session,
    profile,
    // Wait for the first profile load; once it has failed for good, stop
    // blocking the UI (isPending turns false on error).
    isLoading: !isAuthReady || (userId !== null && profileQuery.isPending),
    isAuthenticated: !!user,
    isAdmin: profile?.is_admin ?? false,
    refreshProfile,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

/**
 * Hook to access auth context
 */
export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }

  return context
}

/**
 * Hook to check if user is authenticated
 * Returns loading state while auth is being determined
 */
export function useIsAuthenticated(): { isAuthenticated: boolean; isLoading: boolean } {
  const { isAuthenticated, isLoading } = useAuthContext()
  return { isAuthenticated, isLoading }
}

/**
 * Hook to get current user profile
 */
export function useProfile(): ProfileWithMetadata | null {
  const { profile } = useAuthContext()
  return profile
}

/**
 * Hook to check if user has a specific plan tier or higher
 */
export function useHasTier(requiredTier: 'basic' | 'personal' | 'professional'): boolean {
  const { profile } = useAuthContext()

  if (!profile) return false

  const tierOrder = { basic: 0, personal: 1, professional: 2 }
  return tierOrder[profile.plan_tier] >= tierOrder[requiredTier]
}
